import { useState } from 'react';
import type { Boucle } from '../partage/api/types';
import { useDeplacer, useEmportees } from '../partage/useArbre';
import { Deplacer } from '../inventaire';
import { RefusDeCycle } from '../moteur/conditions';
import { lireLeRefus } from '../projets';
import { useEchap } from '../partage/useEchap';
import { deplacerLaBulle } from '../partage/vocabulaire';
import './DeplacerBulle.css';

/**
 * « Déplacer » depuis la fiche (chantier `deplacer`, ticket 02, décisions d'Igor du 2026-09-29
 * et du 2026-10-01). Le choix de la destination de l'inventaire (`Deplacer`), ouvert au-dessus
 * de la fiche comme la confirmation de suppression : voile clair, panneau au centre. Échap et
 * « Renoncer » le ferment sans rien faire, la fiche reste ouverte.
 *
 * Ce qui part avec elle est demandé au serveur, comme pour supprimer : la fiche ne connaît
 * que ses enfants directs, et c'est tout le sous-arbre qui suit.
 *
 * Si le rangement fermerait une boucle, le serveur refuse sans rien écrire (ticket 01) et le
 * panneau de la boucle prend la place du choix, réduit à « Ne pas déplacer » : il ferme tout,
 * la fiche reste ouverte, rien n'a bougé. Un autre refus (dans sa propre branche, trop
 * profond) se dit sous le choix, qui reste ouvert.
 */
export function DeplacerBulle({
  bulle,
  onRenoncer,
  onDeplacee,
}: {
  bulle: { id: string; titre: string };
  onRenoncer: () => void;
  /** Le serveur a rangé la bulle : la fiche n'a plus rien à montrer d'ici. */
  onDeplacee: () => void;
}) {
  const emportees = useEmportees(bulle.id);
  const deplacer = useDeplacer();
  const [boucle, setBoucle] = useState<Boucle | null>(null);

  // Échap renonce, au choix comme devant la boucle ; la fiche dessous ne l'entend pas.
  useEchap(true, onRenoncer, 'garde');

  if (boucle) {
    return <RefusDeCycle boucle={boucle} geste="deplacement" onFermer={onRenoncer} />;
  }

  const refus = deplacer.error ? lireLeRefus(deplacer.error) : null;
  return (
    <>
      <div className="deplacer-bulle-voile" onClick={onRenoncer} aria-hidden="true" />

      <div
        className="deplacer-bulle"
        data-tuto="deplacer-bulle"
        role="dialog"
        aria-label={deplacerLaBulle(bulle.titre)}
      >
        <h2>{deplacerLaBulle(bulle.titre)}</h2>

        <Deplacer
          bulleId={bulle.id}
          titre={bulle.titre}
          combienDedans={emportees.data?.bulles ?? 0}
          onRenoncer={onRenoncer}
          onDeposer={(parentId) => {
            if (deplacer.isPending) return;
            deplacer.mutate(
              { id: bulle.id, parentId },
              {
                onSuccess: onDeplacee,
                onError: (erreur) => {
                  const lu = lireLeRefus(erreur);
                  if (lu.sorte === 'boucle') setBoucle(lu.boucle);
                },
              }
            );
          }}
        />

        {refus?.sorte === 'dit' && (
          <p className="deplacer-bulle-refus" role="status">
            {refus.mot}
          </p>
        )}
      </div>
    </>
  );
}
